import React, { useState } from 'react';
import { useAuth } from '@monorepo/shared-auth';
import { IconPlus, IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react';
import { removeLeagueMember, updateMemberRole } from '../../services/leagueService';
import InviteMemberModal from './InviteMemberModal';

const MembersTab = ({ leagueId, league, members, isAdmin, onUpdate }) => {
  const { currentUser } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [removing, setRemoving] = useState(false);

  const isTeamLeague = league?.teamBased || false;

  // Separate admins from other members
  const { admins, nonAdmins } = React.useMemo(() => {
    const adminList = members.filter(m => m.role === 'admin');
    const nonAdminList = members.filter(m => m.role !== 'admin');
    return { admins: adminList, nonAdmins: nonAdminList };
  }, [members]);

  // Group members by team for team-based leagues
  const groupedMembers = React.useMemo(() => {
    if (!isTeamLeague) return null;

    const teams = {};
    const unassigned = [];

    nonAdmins.forEach(member => {
      if (member.teamName) {
        if (!teams[member.teamName]) {
          teams[member.teamName] = {
            name: member.teamName,
            captain: null,
            players: []
          };
        }
        if (member.role === 'captain') {
          teams[member.teamName].captain = member;
        } else {
          teams[member.teamName].players.push(member);
        }
      } else {
        unassigned.push(member);
      }
    });

    return { teams: Object.values(teams), unassigned };
  }, [nonAdmins, isTeamLeague]);

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member from the league?')) {
      return;
    }

    setRemoving(true);
    try {
      await removeLeagueMember(leagueId, userId);
      setActiveMenu(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'player' : 'admin';
    const action = newRole === 'admin' ? 'promote to admin' : 'demote to player';

    if (!window.confirm(`Are you sure you want to ${action}?`)) {
      return;
    }

    try {
      await updateMemberRole(leagueId, userId, newRole);
      setActiveMenu(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating member role:', error);
      alert('Failed to update member role. Please try again.');
    }
  };

  const toggleMenu = (userId) => {
    setActiveMenu(activeMenu === userId ? null : userId);
  };

  // Render a single member card
  // Privacy: Only show emails to admins OR show admin emails to everyone (for contact purposes)
  const renderMemberCard = (member) => {
    const canManage = isAdmin && member.userId !== currentUser?.uid;
    const showEmail = isAdmin || member.role === 'admin';

    return (
      <article key={member.userId} className="ls-member-card">
        <div className="ls-member-info">
          <div className="ls-member-avatar">
            {member.firstName?.[0]}{member.lastName?.[0]}
          </div>
          <div className="ls-member-details">
            <h4>{member.firstName} {member.lastName}</h4>
            {showEmail && member.email && (
              <p className="ls-member-email">{member.email}</p>
            )}
            <span className={`ls-role-badge ls-${member.role}`}>
              {member.role === 'admin' ? 'Admin' : member.role === 'captain' ? 'Captain' : 'Player'}
            </span>
          </div>
        </div>

        {canManage && (
          <div className="ls-member-actions">
            <button
              className="ls-btn-menu"
              onClick={() => toggleMenu(member.userId)}
              aria-label="Member options"
            >
              <IconDotsVertical size={20} />
            </button>

            {activeMenu === member.userId && (
              <div className="ls-member-menu">
                <button
                  onClick={() => handleToggleRole(member.userId, member.role)}
                  className="ls-menu-item"
                >
                  <IconEdit size={16} />
                  <span>
                    {member.role === 'admin' ? 'Make Player' : 'Make Admin'}
                  </span>
                </button>
                <button
                  onClick={() => handleRemoveMember(member.userId)}
                  className="ls-menu-item ls-danger"
                  disabled={removing}
                >
                  <IconTrash size={16} />
                  <span>Remove</span>
                </button>
              </div>
            )}
          </div>
        )}
      </article>
    );
  };

  return (
    <section className="ls-members-tab">
      <header className="ls-members-header">
        <h2>League Members ({members.length})</h2>
        {isAdmin && (
          <button
            className="ls-btn-invite"
            onClick={() => setShowInviteModal(true)}
          >
            <IconPlus size={20} />
            <span>Invite Members</span>
          </button>
        )}
      </header>

      {members.length === 0 ? (
        <section className="ls-empty-members">
          <p>No members yet. Invite some players to get started!</p>
        </section>
      ) : (
        <>
          {/* Admins Section - Always shown first */}
          {admins.length > 0 && (
            <section className="ls-admins-section">
              <h3 className="ls-section-title">League Administrators ({admins.length})</h3>
              <div className="ls-members-grid">
                {admins.map(admin => renderMemberCard(admin))}
              </div>
            </section>
          )}

          {/* Participants Section */}
          {nonAdmins.length > 0 && (
            <section className="ls-participants-section">
              <h3 className="ls-section-title">Participants ({nonAdmins.length})</h3>
              {isTeamLeague ? (
                // Team-based view
                <div className="ls-teams-container">
                  {groupedMembers.teams.map(team => (
                    <section key={team.name} className="ls-team-section">
                      <h4 className="ls-team-name">{team.name}</h4>
                      <div className="ls-members-grid">
                        {team.captain && renderMemberCard(team.captain)}
                        {team.players.map(player => renderMemberCard(player))}
                      </div>
                    </section>
                  ))}

                  {groupedMembers.unassigned.length > 0 && (
                    <section className="ls-team-section">
                      <h4 className="ls-team-name">Unassigned Members</h4>
                      <div className="ls-members-grid">
                        {groupedMembers.unassigned.map(member => renderMemberCard(member))}
                      </div>
                    </section>
                  )}
                </div>
              ) : (
                // Individual view
                <div className="ls-members-grid">
                  {nonAdmins.map(member => renderMemberCard(member))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {showInviteModal && (
        <InviteMemberModal
          leagueId={leagueId}
          league={league}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            if (onUpdate) {
              onUpdate();
            }
          }}
        />
      )}
    </section>
  );
};

export default MembersTab;
